import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { FadeIn } from "@/components/FadeIn";

const LABELS = {
  "solid-modal": "Solid Modal",
  "print-modal": "Print Modal",
  chiffon: "Chiffon",
  accessories: "Accessories",
  all: "All Collections",
};

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

const MATERIALS = ["Modal", "Chiffon"];
const AVAILABILITY = [
  { value: "in-stock", label: "In Stock" },
  { value: "out-of-stock", label: "Out of Stock" },
];

export default function Collection() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");
  const filter = searchParams.get("filter");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [material, setMaterial] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [maxPrice, setMaxPrice] = useState(70);
  const [color, setColor] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (category && category !== "all") params.category = category;
    if (material) params.material = material;
    if (availability) params.availability = availability;
    if (color) params.color = color;
    params.max_price = maxPrice;
    if (filter === "new") params.is_new = true;
    if (filter === "bestseller") params.is_bestseller = true;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, sort, material, availability, color, maxPrice, filter]);

  const filtered = useMemo(() => {
    if (!q) return products;
    const term = q.toLowerCase();
    return products.filter(
      (p) => p.name.toLowerCase().includes(term) || p.category_label.toLowerCase().includes(term)
    );
  }, [products, q]);

  const colors = useMemo(() => {
    const map = {};
    products.forEach((p) => p.colors.forEach((c) => (map[c.name] = c.hex)));
    return Object.entries(map).map(([name, hex]) => ({ name, hex }));
  }, [products]);

  const title = q ? `Search: "${q}"` : LABELS[category] || "Collection";

  const FilterPanel = () => (
    <div className="space-y-10">
      <div>
        <h4 className="eyebrow mb-4">Material</h4>
        <div className="flex flex-col gap-3">
          {MATERIALS.map((m) => (
            <label key={m} className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="radio" name="material" checked={material === m} onChange={() => setMaterial(material === m ? null : m)} className="accent-black" />
              {m}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="eyebrow mb-4">Colour</h4>
        <div className="flex flex-wrap gap-3">
          {colors.map((c) => (
            <button key={c.name} onClick={() => setColor(color === c.name ? null : c.name)} title={c.name}
              className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${color === c.name ? "ring-1 ring-offset-2 ring-[#1A1A1A]" : "border-[#E5E5E5]"}`}
              style={{ backgroundColor: c.hex }} />
          ))}
        </div>
      </div>
      <div>
        <h4 className="eyebrow mb-4">Max Price · ${maxPrice}</h4>
        <input type="range" min="40" max="70" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-black" />
      </div>
      <div>
        <h4 className="eyebrow mb-4">Availability</h4>
        <div className="flex flex-col gap-3">
          {AVAILABILITY.map((a) => (
            <label key={a.value} className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="radio" name="availability" checked={availability === a.value} onChange={() => setAvailability(availability === a.value ? null : a.value)} className="accent-black" />
              {a.label}
            </label>
          ))}
        </div>
      </div>
      <button onClick={() => { setMaterial(null); setColor(null); setAvailability(null); setMaxPrice(70); }} className="eyebrow underline">
        Clear all
      </button>
    </div>
  );

  return (
    <div className="container-lira py-14 md:py-20" data-testid="collection-page">
      <FadeIn>
        <div className="text-center mb-12 md:mb-16">
          <p className="eyebrow mb-3">{filtered.length} pieces</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight">{title}</h1>
        </div>
      </FadeIn>

      <div className="flex items-center justify-between mb-10 border-y border-[#E5E5E5] py-4">
        <button className="lg:hidden flex items-center gap-2 text-xs uppercase tracking-[0.2em]" onClick={() => setFiltersOpen(true)} data-testid="open-filters">
          <SlidersHorizontal strokeWidth={1} className="h-4 w-4" /> Filters
        </button>
        <span className="hidden lg:block eyebrow">Refine</span>
        <div className="flex items-center gap-3">
          <span className="eyebrow hidden sm:inline">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm bg-transparent outline-none cursor-pointer" data-testid="sort-select">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block">
          <FilterPanel />
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse"><div className="aspect-[3/4] bg-beige rounded-sm" /><div className="h-4 bg-beige mt-4 w-2/3" /></div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[#737373] py-20">No pieces match your filters.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {filtered.map((p, i) => (
                <FadeIn key={p.id} delay={(i % 3) * 0.08}><ProductCard product={p} /></FadeIn>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-4/5 max-w-sm bg-white p-8 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h3 className="eyebrow">Filters</h3>
              <button onClick={() => setFiltersOpen(false)} aria-label="Close"><X strokeWidth={1} className="h-5 w-5" /></button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}
    </div>
  );
}
